import React, { useState, useEffect, useMemo } from 'react';
import { Table, Text, Container, Spacer, Loading, Button } from "@nextui-org/react";
import { useNavigate } from 'react-router-dom';
import { getWorkshops } from "../../config/workshopsApi";

interface Workshop {
  id: string;
  title: string;
  description: string;
  date: string;
  startTime: string;
  targetDuration: number; // in minutes
  instructorName: string;
  eventCoverUrl: string;
  status?: 'draft' | 'published'; // Optional, might not be in API
}

type FilterStatus = 'All' | 'Upcoming' | 'Live' | 'Completed';

const getWorkshopTimeStatus = (date: string, startTime: string, targetDuration: number): 'Upcoming' | 'Live' | 'Completed' => {
  // Combine date and time
  // Assuming date is "YYYY-MM-DD" and startTime is "HH:mm" or "HH:mm:ss"
  const startDateTimeString = `${date.split('T')[0]}T${startTime}`;
  const start = new Date(startDateTimeString).getTime();

  // If parsing fails, default to Upcoming? Or handle error.
  if (isNaN(start)) return 'Upcoming';

  const durationMs = targetDuration * 60 * 1000;
  const end = start + durationMs;
  const now = Date.now();

  if (start > now) return 'Upcoming';
  if (start <= now && end > now) return 'Live';
  return 'Completed';
};

const WorkshopList = () => {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('All');

  useEffect(() => {
    const fetchWorkshops = async () => {
      setIsLoading(true);

      try {
        const data = await getWorkshops();
        setWorkshops(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching workshops:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkshops();
  }, []);

  const filteredWorkshops = useMemo(() => {
    if (filter === 'All') return workshops;
    return workshops.filter((workshop) => {
      return getWorkshopTimeStatus(workshop.date, workshop.startTime, workshop.targetDuration) === filter;
    });
  }, [workshops, filter]);

  const columns = [
    { name: "WORKSHOP", uid: "title" },
    { name: "INSTRUCTOR", uid: "instructorName" },
    { name: "DATE", uid: "dateTime" },
    { name: "DURATION", uid: "targetDuration" },
    { name: "TIME STATUS", uid: "timeStatus" },
    // Removed "STATUS" (draft/published) column as it's not in the requested fields
  ];

  const renderCell = (item: Workshop, columnKey: React.Key) => {
    const cellValue = item[columnKey as keyof Workshop];
    switch (columnKey) {
      case "title":
        return (
          <Text b size={14} css={{ tt: "capitalize" }}>
            {item.title}
          </Text>
        );
      case "instructorName":
        return (
          <Text b size={14} css={{ tt: "capitalize" }}>
            {item.instructorName}
          </Text>
        );
      case "dateTime":
        return (
          <Text b size={14}>
            {item.date} {item.startTime}
          </Text>
        );
      case "targetDuration":
        return (
          <Text b size={14}>
            {item.targetDuration} min
          </Text>
        );
      case "timeStatus": {
        const status = getWorkshopTimeStatus(item.date, item.startTime, item.targetDuration);
        let color = '$text';
        if (status === 'Live') color = '$error';
        if (status === 'Upcoming') color = '$primary';
        if (status === 'Completed') color = '$neutral';
        return (
          <Text b size={13} css={{ color }}>
            {status.toUpperCase()}
          </Text>
        );
      }
      default:
        return <Text>{cellValue as React.ReactNode}</Text>;
    }
  };

  return (
    <Container md>
      <Spacer y={1} />
      <Text h2>Workshops</Text>
      <Spacer y={1} />
      <Button.Group>
        {['All', 'Upcoming', 'Live', 'Completed'].map((status) => (
          <Button
            key={status}
            size="sm"
            bordered={filter !== status}
            onClick={() => setFilter(status as FilterStatus)}
          >
            {status}
          </Button>
        ))}
      </Button.Group>
      <Spacer y={1} />
      {isLoading ? (
        <Loading type="points" />
      ) : (
        <Table
          aria-label="Workshops table"
          css={{
            height: "auto",
            minWidth: "100%",
          }}
          selectionMode="single"
          onSelectionChange={(selection) => {
            // NextUI v1 selection is a Set or 'all'. For single mode it's a Set.
            if (selection !== "all") {
              const selectedId = Array.from(selection)[0];
              if (selectedId) {
                navigate(`/workshop/${selectedId}`);
              }
            }
          }}
        >
          <Table.Header columns={columns}>
            {(column) => (
              <Table.Column
                key={column.uid}
                align={column.uid === "actions" ? "center" : "start"}
              >
                {column.name}
              </Table.Column>
            )}
          </Table.Header>
          <Table.Body items={filteredWorkshops}>
            {(item: Workshop) => (
              <Table.Row key={item.id}>
                {(columnKey: React.Key) => <Table.Cell>{renderCell(item, columnKey)}</Table.Cell>}
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
};

// Helper for mock data dates
/*
const getMockDate = (offsetDays: number, offsetHours: number = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(d.getHours() + offsetHours);
  return d.toISOString().split('T')[0];
};

const getMockTime = (offsetHours: number = 0) => {
  const d = new Date();
  d.setHours(d.getHours() + offsetHours);
  return d.toTimeString().split(' ')[0].substring(0, 5); // HH:mm
};
*/

export default WorkshopList;
